// components/Navigation.jsx
// Menu de navigation principal avec dropdown et drawer mobile

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home,
  Dumbbell,
  BookOpen,
  Settings,
  Menu,
  Mail,
  X,
  ChevronDown,
  PenTool,
  Brain,
  BookMarked,
  CreditCard,
  TrendingUp,
  User,
  BarChart,
  LayoutDashboard,
  Wand2,
  AlertTriangle,
  Languages,
  ListMusic,
  Clock,
  Sparkles,
  Upload,
  Download,
  LogOut,
  LogIn,
  Euro,
  Layers,
  GitCompare,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../config/routePermissions';
import LangueInterfaceSelector from './LangueInterfaceSelector';
import './Navigation.css';

export default function Navigation() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navRef = useRef(null);

  // Fermer le dropdown quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  // Fermer le drawer quand la route change
  useEffect(() => {
    setIsDrawerOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // Ne pas afficher la navigation sur les pages login/register
  if (!isAuthenticated && (location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/mot-de-passe-oublie' || location.pathname === '/nouveau-mot-de-passe' || location.pathname === '/confirmer-email')) {
    return null;
  }

  // Structure de navigation
  const navigationData = [
    {
      id: 'accueil',
      label: t('nav.accueil'),
      path: '/',
      icon: Home,
    },
    {
      id: 'exercices',
      label: t('nav.exercices'),
      icon: Dumbbell,
      subItems: [
        {
          id: 'conjugaison',
          label: t('nav.conjugaison'),
          path: '/exercice-conjugaison-partielle',
          icon: PenTool,
        },
        {
          id: 'conjugaison-complete',
          label: t('nav.conjugaisonComplete'),
          path: '/exercice-conjugaison-complete',
          icon: BookOpen,
        },
        { separator: true },
        {
          id: 'exercice-matching',
          label: t('nav.matchVocabulaire'),
          path: '/exercice-matching',
          icon: Wand2,
        },

        {
          id: 'quiz-vocabulaire',
          label: t('nav.quizVocabulaire'),
          path: '/exercice-quiz-vocabulaire',
          icon: BookMarked,
          // isConstruction: true,
        },
        {
          id: 'qcm-grammaire',
          label: t('nav.qcmGrammaire'),
          path: '/exercice-quiz-grammaire',
          icon: Brain,
        },
        { separator: true },
        {
          id: 'flash-cards',
          label: t('nav.flashCards'),
          path: '/exercice-flash-cards',
          icon: CreditCard,
          isConstruction: true,
        },
      ],
    },
    {
      id: 'analyse',
      label: t('nav.analyse'),
      path: '/analyse',
      icon: BookOpen,
    },
    {
      id: 'parametres',
      label: t('nav.parametres'),
      icon: Settings,
      subItems: [
        {
          id: 'niveau',
          label: t('nav.niveau'),
          path: '/changer-niveau',
          icon: TrendingUp,
        },
        {
          id: 'playlists',
          label: t('nav.playlists'),
          path: '/playlists',
          icon: ListMusic,
        },
        {
          id: 'statistiques',
          label: t('nav.statistiques'),
          path: '/statistiques',
          icon: BarChart,
          isConstruction: true,
        },
        {
          id: 'abonnement',
          label: t('nav.abonnement'),
          path: '/abonnement',
          icon: CreditCard,
          isConstruction: true,
        },
      ],
    },
    {
      id: 'vocaverb',
      label: t('nav.vocaverb'),
      icon: Dumbbell,
      subItems: [
        {
          id: 'présentation',
          label: t('nav.presentation'),
          path: '/presentation',
          icon: PenTool,
        },
        {
          id: 'tarifs',
          label: t('nav.tarifs'),
          path: '/tarifs',
          icon: Euro,
        },
        {
          id: 'conditionsgenerales',
          label: t('nav.conditionsGenerales'),
          path: '/conditions-generales',
          icon: BookOpen,
        },
        {
          id: 'mentions',
          label: t('nav.mentionsLegales'),
          path: '/mentions-legales',
          icon: Layers,
        },
        {
          id: 'contact',
          label: t('nav.contact'),
          path: '/contact',
          icon: Mail,
        },
      ],
    },
    {
      id: 'gestion',
      label: 'Gestion',
      icon: Settings,
      subItems: [
        {
          id: 'dashboard',
          label: 'Tableau de bord',
          path: '/gestion/dashboard',
          icon: LayoutDashboard,
        },
        { separator: true },
        {
          id: 'gestion-mots',
          label: 'Gestion des mots',
          path: '/gestion/mots',
          icon: BookOpen,
        },
        {
          id: 'coherence-verbes',
          label: 'Cohérence Mots/Verbes',
          path: '/gestion/coherence-verbes',
          icon: GitCompare,
        },
        {
          id: 'mots-signales',
          label: 'Mots signalés',
          path: '/gestion/mots-signales',
          icon: AlertTriangle,
        },
        {
          id: 'questions-signalees',
          label: 'Questions signalées',
          path: '/gestion/questions-signalees',
          icon: AlertTriangle,
        },
        {
          id: 'themes-generiques',
          label: 'Themes / Generiques',
          path: '/gestion/themes-generiques',
          icon: Layers,
        },
        {
          id: 'mots-pending',
          label: 'Mots en attente',
          path: '/gestion/mots-pending',
          icon: Clock,
        },
        { separator: true },
        {
          id: 'enrichissement-complet',
          label: 'Enr. mots IA',
          path: '/gestion/enrichissement-complet',
          icon: Sparkles,
        },
        {
          id: 'enrichissement',
          label: 'Enr. verbes IA',
          path: '/enrichissement',
          icon: Wand2,
        },
        { separator: true },
        {
          id: 'Export',
          label: 'Export BDD',
          path: '/gestion/export',
          icon: Download,
        },
        {
          id: 'import',
          label: 'Import BDD',
          path: '/gestion/import',
          icon: Upload,
        },
      ],
    },
  ];

  // Filtrer les items selon les permissions de l'utilisateur
  const niveauAcces = user?.niveauAcces || 'gratuit';
  const filteredNavigationData = navigationData
    .map((item) => {
      // Item simple : vérifier la permission
      if (item.path && !hasPermission(item.path, niveauAcces)) {
        return null;
      }
      // Item avec sous-menu : filtrer les sous-items
      if (item.subItems) {
        const filteredSubItems = item.subItems.filter((sub) => sub.separator || !sub.path || hasPermission(sub.path, niveauAcces));
        // Supprimer les séparateurs orphelins (en début, fin, ou consécutifs)
        const cleanedSubItems = filteredSubItems.filter((sub, i, arr) => {
          if (!sub.separator) return true;
          if (i === 0 || i === arr.length - 1) return false;
          if (arr[i - 1]?.separator) return false;
          return true;
        });
        // Si plus aucun sous-item réel, masquer le parent
        if (cleanedSubItems.every((sub) => sub.separator)) return null;
        return { ...item, subItems: cleanedSubItems };
      }
      return item;
    })
    .filter(Boolean);

  // Vérifier si un item est actif
  const isItemActive = (item) => {
    if (item.path) {
      return location.pathname === item.path;
    }
    if (item.subItems) {
      return item.subItems.some((subItem) => location.pathname === subItem.path);
    }
    return false;
  };

  // Toggle dropdown
  const toggleDropdown = (itemId) => {
    setOpenDropdown(openDropdown === itemId ? null : itemId);
  };

  // Fermer le drawer (mobile)
  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setOpenDropdown(null);
  };

  // Gérer le clic sur un item de niveau 2
  const handleSubItemClick = (path) => {
    // Si on est déjà sur cette page, naviguer avec un state pour forcer la réinitialisation
    if (location.pathname === path) {
      navigate(path, { replace: true, state: { resetToScreen1: Date.now() } });
    } else {
      navigate(path);
    }
    setOpenDropdown(null);
    setIsDrawerOpen(false);
  };

  // Déconnexion
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Rendu d'un item de navigation
  const renderNavItem = (item, isMobile = false) => {
    const Icon = item.icon;
    const isActive = isItemActive(item);

    // Item avec sous-menu
    if (item.subItems) {
      const isOpen = openDropdown === item.id;

      return (
        <div key={item.id} className={`nav-item-wrapper ${isActive ? 'active' : ''}`}>
          <button onClick={() => toggleDropdown(item.id)} className={`nav-item ${isActive ? 'active' : ''} ${item.subItems ? 'has-submenu' : ''}`}>
            <Icon size={20} />
            <span className="nav-label">{item.label}</span>
            {item.subItems && <ChevronDown size={16} className={`chevron ${isOpen ? 'open' : ''}`} />}
          </button>

          {/* Dropdown */}
          {isOpen && (
            <div className={`dropdown ${isMobile ? 'mobile' : ''}`}>
              {item.subItems.map((subItem, index) => {
                if (subItem.separator) {
                  return <hr key={`sep-${index}`} className="dropdown-separator" />;
                }

                const SubIcon = subItem.icon;
                const isSubActive = location.pathname === subItem.path;

                return (
                  <button
                    key={subItem.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSubItemClick(subItem.path);
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      handleSubItemClick(subItem.path);
                    }}
                    className={`dropdown-item ${isSubActive ? 'active' : ''} ${subItem.isConstruction ? 'construction' : ''}`}
                  >
                    <SubIcon size={18} />
                    <span>{subItem.label}</span>
                    {subItem.isConstruction && <span className="construction-badge">{t('nav.bientot')}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Item simple (lien direct)
    return (
      <Link key={item.id} to={item.path} onClick={isMobile ? closeDrawer : undefined} className={`nav-item ${isActive ? 'active' : ''}`}>
        <Icon size={20} />
        <span className="nav-label">{item.label}</span>
      </Link>
    );
  };

  return (
    <>
      {/* NAVIGATION DESKTOP */}
      <nav className="navigation desktop" ref={navRef}>
        <div className="desktop-header">
          <BookOpen size={32} color="#4F46E5" />
          <h1>VocaVerb</h1>
        </div>
        <div className="nav-container">
          {filteredNavigationData.map((item) => renderNavItem(item, false))}
          <div className="nav-user-section">
            <LangueInterfaceSelector className="nav-langue-select" />
            {isAuthenticated ? (
              <>
                <Link to="/profil" className="nav-user-name">{user?.prenom || user?.email}</Link>
                <button onClick={handleLogout} className="nav-logout-btn" title={t('nav.seDeconnecter')}>
                  <LogOut size={18} />
                </button>
              </>
            ) : (
              <Link to="/login" className="nav-login-btn">
                <LogIn size={18} />
                <span>{t('auth.seConnecter')}</span>
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* NAVIGATION MOBILE - BURGER + DRAWER */}
      <nav className="navigation mobile">
        {/* Conteneur pour burger + book + titre */}
        <div className="mobile-title">
          <button onClick={() => setIsDrawerOpen(!isDrawerOpen)} className="burger-button" aria-label="Menu">
            <Menu size={24} />
          </button>
          <BookOpen size={32} color="#4F46E5" />
          <h1>VocaVerb</h1>
        </div>

        {/* Overlay */}
        {isDrawerOpen && <div className="drawer-overlay" onClick={closeDrawer} />}

        {/* Drawer */}
        <div className={`drawer ${isDrawerOpen ? 'open' : ''}`}>
          <div className="drawer-header">
            <h2>{t('nav.menu')}</h2>
            <button onClick={closeDrawer} className="close-button">
              <X size={24} />
            </button>
          </div>
          <div className="drawer-content">
            {filteredNavigationData.map((item) => renderNavItem(item, true))}
            <div className="drawer-user-section">
              <LangueInterfaceSelector className="drawer-langue-select" />
              {isAuthenticated ? (
                <>
                  <Link to="/profil" className="drawer-user-info" onClick={closeDrawer}>
                    <User size={18} />
                    <span>{user?.prenom || user?.email}</span>
                  </Link>
                  <button onClick={handleLogout} className="drawer-logout-btn">
                    <LogOut size={18} />
                    <span>{t('nav.seDeconnecter')}</span>
                  </button>
                </>
              ) : (
                <Link to="/login" className="drawer-login-btn" onClick={closeDrawer}>
                  <LogIn size={18} />
                  <span>{t('auth.seConnecter')}</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}
